import { CartAddEvent } from '@theme/events';

/**
 * SOLE lean AJAX add-to-cart (Phase 1, increment 2b; cart-flow remediation #11/#15).
 * Intercepts native product-form submits → POST /cart/add.js (carrying all line-item
 * properties, incl. SOLE Care/Prosuro `_PlanOptions` when present) → dispatches CartAddEvent
 * so <cart-drawer-component auto-open> opens and <cart-items-component> live-renders.
 * Progressive enhancement: if anything fails, falls back to the native full-page submit.
 *
 * #11: replaced window.alert add-errors with an accessible toast; adds that don't open the
 *      drawer (auto-open off) get a "Added to cart" confirmation toast instead of silence.
 */

const DRAWER_SECTION_ID = 'cart-drawer';

/**
 * Brief highlight pulse on the line(s) matching the just-added variant (drawer + cart page), so the
 * eye finds the new/incremented row after the morph. Reduced-motion disables the pulse via CSS.
 * @param {string|null} variantId
 */
function highlightAddedRow(variantId) {
  if (!variantId) return;
  document.querySelectorAll(`cart-quantity-selector-component[data-variant-id="${variantId}"]`).forEach((sel) => {
    const row = sel.closest('.sole-cart__item');
    if (!row) return;
    row.classList.remove('just-added');
    void row.offsetWidth; // reflow so the animation restarts if the class lingered
    row.classList.add('just-added');
    row.addEventListener('animationend', () => row.classList.remove('just-added'), { once: true });
  });
}

function updateHeaderBubble(itemCount) {
  document.querySelectorAll('.sole-header__cart-count').forEach((el) => {
    el.textContent = itemCount;
    el.classList.toggle('is-empty', !itemCount);
  });
}

/**
 * #15: derive the live cart item count from the re-rendered drawer section that /cart/add.js already
 * returned (it carries `<span ref="cartItemCount">{{ cart.item_count }}</span>`), instead of a second
 * /cart.js round-trip that could lag or fail silently.
 * @param {Record<string, string>|undefined} sections
 * @returns {number|null}
 */
function itemCountFromSections(sections) {
  const html = sections?.[DRAWER_SECTION_ID];
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const el = doc.querySelector('[ref="cartItemCount"]');
  const n = el ? parseInt(el.textContent || '', 10) : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * Shows a transient toast in the global #sole-toast-region (rendered by sections/cart-drawer.liquid).
 * @param {string} message
 * @param {{ isError?: boolean, actionHref?: string, actionLabel?: string }} [opts]
 */
function showToast(message, opts = {}) {
  const { isError = false, actionHref, actionLabel } = opts;
  let region = document.getElementById('sole-toast-region');
  if (!region) {
    region = document.createElement('div');
    region.id = 'sole-toast-region';
    region.className = 'sole-toast-region';
    document.body.appendChild(region);
  }

  const toast = document.createElement('div');
  toast.className = 'sole-toast' + (isError ? ' sole-toast--error' : '');
  // role=alert (assertive) for errors, role=status (polite) for confirmations.
  toast.setAttribute('role', isError ? 'alert' : 'status');

  const text = document.createElement('span');
  text.textContent = message;
  toast.appendChild(text);

  if (actionHref && actionLabel) {
    const link = document.createElement('a');
    link.className = 'sole-toast__link';
    link.href = actionHref;
    link.textContent = actionLabel;
    toast.appendChild(link);
  }

  region.appendChild(toast);

  // The toast is pinned bottom-centre and its CTA is transactional, so fixed UI must move

  // out of its way rather than compete on z-index.

  if (window.SoleSafeBottom) window.SoleSafeBottom.set('cart-toast', region.offsetHeight + 16);
  requestAnimationFrame(() => toast.classList.add('is-visible'));

  const dismiss = () => {
    toast.classList.remove('is-visible');
    const remove = () => toast.remove();
    toast.addEventListener('transitionend', remove, { once: true });
    setTimeout(remove, 400); // fallback if transitionend doesn't fire
  };
  setTimeout(dismiss, isError ? 6000 : 3500);
}

async function handleSubmit(form, submitButton) {
  const formData = new FormData(form);
  // Ask Shopify to also return the re-rendered cart sections so EVERY visible surface morphs in place:
  // the drawer (always) PLUS any cart-items-component on the page (e.g. /cart) — otherwise the cart page
  // would have to do a fallback second fetch to refresh after an add.
  const sectionIds = new Set([DRAWER_SECTION_ID]);
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((c) => {
    if (c instanceof HTMLElement && c.dataset.sectionId) sectionIds.add(c.dataset.sectionId);
  });
  formData.set('sections', Array.from(sectionIds).join(','));
  formData.set('sections_url', window.location.pathname);

  submitButton?.setAttribute('aria-busy', 'true');
  submitButton?.classList.add('is-loading');

  // Theme.routes.cart_add_url / cart_change_url already include ".js"; cart_url does not.
  const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const cartUrl = (window.Theme?.routes?.cart_url || '/cart').replace(/(\.js)?$/, '.js');

  // Respect the merchant's auto-open setting: only optimistically open the drawer when it's
  // configured to auto-open, OR when it's already open (e.g. one-tap accessory add inside it).
  const drawer = document.querySelector('cart-drawer-component');
  const dialog = drawer?.querySelector('dialog[ref="dialog"]');
  const wasOpenBefore = dialog?.open === true;
  const willOpenDrawer = !!drawer && (drawer.hasAttribute('auto-open') || wasOpenBefore);

  if (willOpenDrawer && typeof drawer.open === 'function') {
    drawer.open();
    dialog?.setAttribute('data-sole-loading', '');
  }

  try {
    const addRes = await fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const addJson = await addRes.json();

    if (!addRes.ok) {
      // Shopify returns { status, message, description } on error (e.g. sold out).
      const msg = addJson?.description || addJson?.message || 'Sorry — that item could not be added.';
      dialog?.removeAttribute('data-sole-loading');
      // Close the drawer if it was opened JUST for this (now-failed) add, so the customer isn't left
      // staring at the prior/empty cart. (Do NOT dispatch a CartAddEvent here — auto-open would reopen it.)
      if (willOpenDrawer && !wasOpenBefore && typeof drawer.close === 'function') drawer.close();
      showToast(msg, { isError: true });
      return;
    }

    // Derive the new cart count from the re-rendered drawer section (reused for the header bubble).
    const derivedCount = itemCountFromSections(addJson.sections);

    // Morph every requested cart surface IMMEDIATELY from the add response — do NOT await /cart.js
    // (that second round-trip was the delay; Horizon opens on the add response too). Pass the count as
    // `resource.item_count` so the drawer announces it to screen readers when it opens.
    const qty = parseInt(formData.get('quantity') || '1', 10);
    document.dispatchEvent(
      new CartAddEvent(
        { item_count: derivedCount != null ? derivedCount : undefined },
        form.id,
        {
          source: 'sole-cart-ajax',
          itemCount: qty,
          sections: addJson.sections, // morphs each requested cart-items-component section in place
        }
      )
    );

    // Highlight the added/incremented row once the morph has painted.
    const addedVariantId = formData.get('id');
    setTimeout(() => highlightAddedRow(addedVariantId ? String(addedVariantId) : null), 260);

    if (willOpenDrawer) {
      // Content has morphed in — clear the loading overlay (small delay lets the morph paint).
      setTimeout(() => dialog?.removeAttribute('data-sole-loading'), 200);
    } else {
      // Drawer stays closed → confirm the add with a toast so it isn't silent.
      const addedTitle = addJson?.product_title || addJson?.title;
      const msg = addedTitle ? `Added “${addedTitle}” to cart` : 'Added to cart';
      showToast(msg, { actionHref: window.Theme?.routes?.cart_url || '/cart', actionLabel: 'View cart' });
    }

    // Update the header bubble from the same derived count (#15). Only fall back to a /cart.js fetch
    // if that count couldn't be parsed.
    if (derivedCount != null) {
      updateHeaderBubble(derivedCount);
    } else {
      fetch(cartUrl)
        .then((r) => r.json())
        .then((cart) => updateHeaderBubble(cart.item_count))
        .catch(() => {});
    }
  } catch (e) {
    // Network/parse failure → let the native form submit so the customer is never stuck.
    console.error('[sole-cart-ajax] add failed, falling back to native submit', e);
    dialog?.removeAttribute('data-sole-loading');
    form.submit();
  } finally {
    submitButton?.removeAttribute('aria-busy');
    submitButton?.classList.remove('is-loading');
  }
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return; // only product add-to-cart forms

    event.preventDefault();
    const submitButton = form.querySelector('[type="submit"]');
    handleSubmit(form, submitButton);
  },
  // capture so we win before any other submit handler
  true
);

/**
 * Live "Remove" in the drawer + cart page. The remove control is an <a href="/cart/change…"> so it
 * works with JS off; with JS we intercept it and call the harvested <cart-items-component>'s own
 * `onLineItemRemove(line)` DIRECTLY (animates the row, morphs the section, shows the empty state).
 *
 * We deliberately do NOT dispatch a QuantitySelectorUpdateEvent here: that path runs through the
 * component's single shared 300ms debounce, so removing two different lines within 300ms would collapse
 * to just the last one and silently drop a removal. A direct call removes immediately (and the
 * component disables itself during each in-flight change, which naturally serializes rapid removes).
 * Falls through to the link's native navigation if the live component isn't available.
 */
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const removeLink = target.closest('.sole-cart__item-remove');
  if (!removeLink) return;

  const component = removeLink.closest('cart-items-component');
  // Only hijack when the live cart component (with its removal API) is present; else let the link navigate.
  if (!component || typeof (/** @type {any} */ (component).onLineItemRemove) !== 'function') return;

  event.preventDefault();

  // Serialize cart mutations: `/cart/change.js` uses POSITIONAL line numbers, so a second remove fired
  // while the first is in flight would race (line 2 shifts to line 1 once line 1 is gone) and drop one.
  // The component flags itself `cart-items-disabled` during an in-flight change — ignore clicks then; the
  // user re-clicks once it settles (the item is still visible, never silently dropped).
  if (component.classList.contains('cart-items-disabled')) return;

  const row = removeLink.closest('[data-line]');
  const line = parseInt(row?.getAttribute('data-line') || '', 10);
  if (!Number.isFinite(line) || line < 1) return; // bad line → native navigation fallback

  /** @type {any} */ (component).onLineItemRemove(line);
});

// Tell the early inline capture-guard (sections/cart-drawer.liquid) that the AJAX handler is live, so
// any submit it held back (a click that beat this module) is now replayed through the interceptor.
window.__soleCartAjaxReady = true;
window.dispatchEvent(new Event('sole:cart-ajax-ready'));
